import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface MeetingListProps {
  companyId?: string;
  settings: any[];
}

const MeetingList: React.FC<MeetingListProps> = ({ companyId, settings }) => {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    meeting_date: '',
    meeting_title: '',
    meeting_code: '',
    start_time: '',
    end_time: '',
    minutes_by: '',
    presentation_link: '',
    next_meeting_date: '',
    agenda: '',
    discussion_points: '',
    decisions: ''
  });

  useEffect(() => {
    if (companyId) fetchMeetings();
  }, [companyId]);

  const fetchMeetings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('mom_meetings')
      .select('*')
      .eq('company_id', companyId)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) setMeetings(data);
    setLoading(false);
  };

  const handleSaveMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    try {
      const { error } = await supabase
        .from('mom_meetings')
        .insert([{ ...formData, company_id: companyId }]);
      
      if (error) throw error;
      setShowForm(false);
      setFormData({
        meeting_date: '', meeting_title: '', meeting_code: '', 
        start_time: '', end_time: '', minutes_by: '', 
        presentation_link: '', next_meeting_date: '',
        agenda: '', discussion_points: '', decisions: ''
      });
      fetchMeetings();
    } catch (err) {
      console.error('Error saving meeting:', err);
      alert('Failed to save meeting');
    }
  };

  if (loading) return <div>Loading meetings...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem' }}>Meeting Details</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Meeting'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSaveMeeting} style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '8px', marginBottom: '2rem', border: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Meeting Title</label>
              <input required type="text" value={formData.meeting_title} onChange={e => setFormData({...formData, meeting_title: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Meeting Date</label>
              <input required type="date" value={formData.meeting_date} onChange={e => setFormData({...formData, meeting_date: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Meeting Code</label>
              <input type="text" value={formData.meeting_code} onChange={e => setFormData({...formData, meeting_code: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Minutes By</label>
              <select value={formData.minutes_by} onChange={e => setFormData({...formData, minutes_by: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
                <option value="">Select Team Member</option>
                {settings.filter(s => s.category === 'Team').map(s => (
                  <option key={s.id} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Start Time</label>
              <input type="time" value={formData.start_time} onChange={e => setFormData({...formData, start_time: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>End Time</label>
              <input type="time" value={formData.end_time} onChange={e => setFormData({...formData, end_time: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Presentation Link</label>
              <input type="url" value={formData.presentation_link} onChange={e => setFormData({...formData, presentation_link: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Next Meeting Date</label>
              <input type="date" value={formData.next_meeting_date} onChange={e => setFormData({...formData, next_meeting_date: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Agenda</label>
              <textarea value={formData.agenda} onChange={e => setFormData({...formData, agenda: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} rows={3} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Discussion Points</label>
              <textarea value={formData.discussion_points} onChange={e => setFormData({...formData, discussion_points: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} rows={3} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Decisions</label>
              <textarea value={formData.decisions} onChange={e => setFormData({...formData, decisions: e.target.value})} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }} rows={3} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Save Meeting</button>
        </form>
      )}

      {meetings.length === 0 ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '2rem' }}>No meetings found. Create one to get started.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Title</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Code</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Time</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Minutes By</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Discussion Points</th>
                <th style={{ padding: '1rem', fontWeight: 600 }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {meetings.map((m, i) => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '1rem' }}>{new Date(m.meeting_date).toLocaleDateString()}</td>
                  <td style={{ padding: '1rem' }}>{m.meeting_title}</td>
                  <td style={{ padding: '1rem' }}>{m.meeting_code || '-'}</td>
                  <td style={{ padding: '1rem' }}>{m.start_time} - {m.end_time}</td>
                  <td style={{ padding: '1rem' }}>{m.minutes_by || '-'}</td>
                  <td style={{ padding: '1rem', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.discussion_points}>
                    {m.discussion_points || '-'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {m.presentation_link ? <a href={m.presentation_link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>View</a> : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MeetingList;
